import {
  Accordion,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  HStack,
  RadioGroup,
  NativeSelect,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  NumberInput,
  Field,
  type RadioGroupValueChangeDetails,
  type NumberInputValueChangeDetails,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { extractApiMessage } from '../../lib/apiError'
import { explainCopy } from '../../lib/explainCopy'
import { useTurfs } from '../../hooks/useTurf'
import { useContactLists } from '../../hooks/useContactList'
import { useTargetingCount } from '../../hooks/useElectionSim'
import { FLAG_KEYS, flagLabel } from '../../types/analyticsFlags'
import { SMART_FILTER_DEFAULTS, PRIMARY_SMART_FILTER_DEFAULTS, PRIMARY_ISSUES } from '../../types/electionSim'
import { gotvSweetSpotTargeting, whereEffortHelpsTargeting } from './targetingPresets'
import type {
  AdvancedLevers,
  Assumptions,
  CapacityPlan,
  ContestRequest,
  SmartFilter,
  Targeting,
  TargetingKind,
} from '../../types/electionSim'

const fmt = (n: number) => new Intl.NumberFormat('en-US').format(n)

// Propensity mid-band the turnout-lift assumption applies to. A model constant (matches the
// mockup's "30–60% modeled vote propensity" copy), surfaced here so the tooltip reads honestly.
const BAND_LOWER = 30
const BAND_UPPER = 60

export interface Levers {
  capacity: CapacityPlan
  targeting: Targeting
  assumptions: Assumptions
  advanced: AdvancedLevers | null
}

export interface LeverPanelProps {
  levers: Levers
  onChange: (levers: Levers) => void
  onRun: () => void
  isRunning: boolean
  /** Reachable-voter count from the latest resolved run; null before any run. */
  reachable: number | null
  /** Contest used to key the live reachable-count query; null disables the live count. */
  contestForCount: ContestRequest | null
  /** GeoUnit ids of the last run's positive-net precincts; drives the "Where effort helps" preset.
   *  Empty (no run yet, or no positive precinct) disables that preset. */
  positiveNetPrecinctIds: string[]
  /** Primary-race mode: swaps the smart-filter section to primary controls. */
  isPrimary: boolean
}

/** A lever label whose text is the hover target for its transparency tooltip. The dashed
 * underline mirrors the mockup's `.tip-anchor`; `openDelay={0}` keeps it testable. */
function LeverLabel({ text, tip }: { text: string; tip: string }) {
  return (
    <Tooltip content={tip} openDelay={0} showArrow contentProps={{ maxW: "320px" }} positioning={{
      placement: "top"
    }}>
      <Text
        as="span"
        tabIndex={0}
        cursor="help"
        borderBottomWidth="1px"
        borderBottomStyle="dashed"
        borderBottomColor="accent"
      >
        {text}
      </Text>
    </Tooltip>
  );
}

interface NumberFieldProps {
  label: string
  tip: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  helper?: string
  muted?: boolean
}

function NumberField({ label, tip, value, onChange, min = 0, max, helper, muted }: NumberFieldProps) {
  return (
    <Field.Root>
      <Field.Label fontSize="sm" mb={1} color={muted ? 'gray.500' : undefined}>
        <LeverLabel text={label} tip={tip} />
      </Field.Label>
      <NumberInput.Root
        size="sm"
        value={String(value)}
        min={min}
        max={max}
        onValueChange={(details: NumberInputValueChangeDetails) => onChange(Number.isNaN(details.valueAsNumber) ? 0 : details.valueAsNumber)}
      >
        <NumberInput.Input aria-label={label} />
      </NumberInput.Root>
      {!!helper && (
        <Field.HelperText fontSize="xs" color="gray.500" mt={1}>
          {helper}
        </Field.HelperText>
      )}
    </Field.Root>
  );
}

function Card({ eyebrow, children }: { eyebrow: string; children: React.ReactNode }) {
  return (
    <Box bg="surface" p={5} borderRadius="lg" shadow="md" w="full">
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="0.08em"
        textTransform="uppercase"
        color="gray.500"
        mb={3}
      >
        {eyebrow}
      </Text>
      {children}
    </Box>
  )
}

export function LeverPanel({ levers, onChange, onRun, isRunning, reachable, contestForCount, positiveNetPrecinctIds, isPrimary }: LeverPanelProps) {
  const turfsQuery = useTurfs()
  const listsQuery = useContactLists()
  const turfs = turfsQuery.data ?? []
  const lists = listsQuery.data ?? []

  const smartDefaults = isPrimary ? PRIMARY_SMART_FILTER_DEFAULTS : SMART_FILTER_DEFAULTS
  const sf: SmartFilter = levers.targeting.smartFilter ?? smartDefaults
  const countReq =
    levers.targeting.kind === 'smartfilter' && contestForCount?.districtGeoUnitId
      ? { contest: contestForCount, targeting: levers.targeting }
      : null
  const countQuery = useTargetingCount(countReq)

  const { canvassers, shiftsPerWeek, weeks, doorsPerShift, contactRatePct } = levers.capacity
  const shifts = canvassers * shiftsPerWeek * weeks
  const doors = shifts * doorsPerShift
  const contacts = Math.round((doors * contactRatePct) / 100)

  function setCapacity(patch: Partial<CapacityPlan>) {
    onChange({ ...levers, capacity: { ...levers.capacity, ...patch } })
  }
  function setTargeting(patch: Partial<Targeting>) {
    onChange({ ...levers, targeting: { ...levers.targeting, ...patch } })
  }
  function setSmartFilter(patch: Partial<SmartFilter>) {
    setTargeting({ smartFilter: { ...sf, ...patch } })
  }
  function setAssumptions(patch: Partial<Assumptions>) {
    onChange({ ...levers, assumptions: { ...levers.assumptions, ...patch } })
  }
  function setAdvanced(patch: Partial<AdvancedLevers>) {
    onChange({ ...levers, advanced: { ...(levers.advanced ?? {}), ...patch } })
  }
  function applyPreset(targeting: Targeting) {
    onChange({ ...levers, targeting })
  }
  const whereEffortDisabled = positiveNetPrecinctIds.length === 0

  const capacityTotalTip = explainCopy.capacityTotal(shifts, doors, contacts)

  return (
    <VStack align="stretch" gap={4}>
      {/* ---------------------------------------------------------------- */}
      {/* Field capacity */}
      {/* ---------------------------------------------------------------- */}
      <Card eyebrow="Field capacity">
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          <NumberField
            label="Canvassers"
            tip={capacityTotalTip}
            value={canvassers}
            onChange={(v) => setCapacity({ canvassers: v })}
          />
          <NumberField
            label="Shifts / week"
            tip={capacityTotalTip}
            value={shiftsPerWeek}
            onChange={(v) => setCapacity({ shiftsPerWeek: v })}
          />
          <NumberField
            label="Weeks"
            tip={capacityTotalTip}
            value={weeks}
            onChange={(v) => setCapacity({ weeks: v })}
          />
          <NumberField
            label="Doors per shift"
            tip={explainCopy.doorsPerShift(doorsPerShift, contactRatePct)}
            value={doorsPerShift}
            onChange={(v) => setCapacity({ doorsPerShift: v })}
          />
          <NumberField
            label="Contact rate"
            tip={explainCopy.contactRate(doors, contactRatePct, doors)}
            value={contactRatePct}
            min={0}
            max={100}
            onChange={(v) => setCapacity({ contactRatePct: v })}
          />
        </SimpleGrid>

        <Tooltip content={capacityTotalTip} openDelay={0} showArrow contentProps={{ maxW: "320px" }} positioning={{
          placement: "top"
        }}>
          <Box
            mt={4}
            borderLeftWidth="3px"
            borderLeftColor="info.fg"
            bg="gray.50"
            borderRadius="0 8px 8px 0"
            px={4}
            py={2}
            cursor="help"
            tabIndex={0}
          >
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              {`≈ ${fmt(contacts)} contacts`}
            </Text>
            <Text fontSize="xs" color="gray.600" mt={0.5}>
              {`${fmt(shifts)} shifts × ${fmt(doorsPerShift)} doors/shift = ${fmt(doors)} doors, × ${contactRatePct}% contact rate`}
            </Text>
          </Box>
        </Tooltip>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Targeting */}
      {/* ---------------------------------------------------------------- */}
      <Card eyebrow="Targeting — who gets knocked">
        <HStack gap={2} mb={3} align="stretch">
          <Tooltip content={explainCopy.presetGotvSweetSpot()} openDelay={0} showArrow contentProps={{ maxW: "320px" }} positioning={{
            placement: "top"
          }}>
            <Button
              size="sm"
              colorPalette="brand"
              onClick={() => applyPreset(gotvSweetSpotTargeting())}
            >
              GOTV sweet spot
            </Button>
          </Tooltip>
          <Tooltip content={explainCopy.presetWhereEffortHelps()} openDelay={0} showArrow contentProps={{ maxW: "320px" }} positioning={{
            placement: "top"
          }}>
            {/* span wrapper: a disabled Button has pointer-events:none, so the Tooltip needs a live host. */}
            <Box as="span" tabIndex={0}>
              <Button
                size="sm"
                variant="outline"
                colorPalette="brand"
                disabled={whereEffortDisabled}
                onClick={() => applyPreset(whereEffortHelpsTargeting(positiveNetPrecinctIds))}
              >
                Where effort helps
              </Button>
            </Box>
          </Tooltip>
        </HStack>

        <RadioGroup.Root
          value={levers.targeting.kind}
          onValueChange={(details: RadioGroupValueChangeDetails) => {
            const kind = details.value as TargetingKind
            setTargeting(
              kind === 'smartfilter' && !levers.targeting.smartFilter
                ? { kind, smartFilter: smartDefaults }
                : { kind },
            )
          }}>
          <Stack gap={2} fontSize="sm">
            <RadioGroup.Item value="geography"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Whole district</RadioGroup.ItemText></RadioGroup.Item>
            <RadioGroup.Item value="precincts"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Map-selected precincts</RadioGroup.ItemText></RadioGroup.Item>
            <RadioGroup.Item value="turf"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Turf</RadioGroup.ItemText></RadioGroup.Item>
            <RadioGroup.Item value="list"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Saved list</RadioGroup.ItemText></RadioGroup.Item>
            <RadioGroup.Item value="flags"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Flag cohort</RadioGroup.ItemText></RadioGroup.Item>
            <RadioGroup.Item value="smartfilter"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>
                <LeverLabel text="Smart filter" tip={explainCopy.smartFilterMode()} />
              </RadioGroup.ItemText></RadioGroup.Item>
          </Stack>
        </RadioGroup.Root>

        {levers.targeting.kind === 'precincts' && (
          <Text fontSize="xs" color="gray.500" mt={3}>
            Uses the precincts selected on the analytics map.
          </Text>
        )}

        {levers.targeting.kind === 'turf' && (
          <Field.Root mt={3}>
            <Field.Label fontSize="sm" mb={1}>
              Turf
            </Field.Label>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={levers.targeting.turfId ?? ''}
                onChange={(e) => setTargeting({ turfId: e.target.value || null })}
                placeholder={turfsQuery.isLoading ? 'Loading turfs…' : 'Select a turf'}>
                {turfs.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        )}

        {levers.targeting.kind === 'list' && (
          <Field.Root mt={3}>
            <Field.Label fontSize="sm" mb={1}>
              Saved list
            </Field.Label>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={levers.targeting.listId ?? ''}
                onChange={(e) => setTargeting({ listId: e.target.value || null })}
                placeholder={listsQuery.isLoading ? 'Loading lists…' : 'Select a list'}>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        )}

        {levers.targeting.kind === 'flags' && (
          <Box mt={3}>
            <Field.Label fontSize="sm" mb={2}>
              Flag cohort
            </Field.Label>
            <CheckboxGroup
              value={levers.targeting.flagKeys ?? []}
              onValueChange={(vals: string[]) => setTargeting({ flagKeys: vals as string[] })}
            >
              <SimpleGrid columns={{ base: 1, sm: 2 }} gap={2}>
                {FLAG_KEYS.map((key) => (
                  <Checkbox.Root key={key} value={key} size="sm"><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                    {flagLabel(key)}
                  </Checkbox.Label></Checkbox.Root>
                ))}
              </SimpleGrid>
            </CheckboxGroup>
          </Box>
        )}

        {levers.targeting.kind === 'smartfilter' && (
          <VStack align="stretch" gap={4} mt={3}>
            {isPrimary ? (
              <>
                <Checkbox.Root
                  size="sm"
                  onCheckedChange={(details) => setSmartFilter({ registeredDemOnly: details.checked === true })}
                  checked={sf.registeredDemOnly ?? true}
                ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                  <LeverLabel text="Registered Democrats only" tip={explainCopy.primaryRegisteredDem()} />
                </Checkbox.Label></Checkbox.Root>

                <Field.Root>
                  <Field.Label fontSize="sm" mb={1}>
                    <LeverLabel text="Primary turnout propensity" tip={explainCopy.primaryTurnoutBand(sf.propensityLo, sf.propensityHi)} />
                  </Field.Label>
                  <SimpleGrid columns={2} gap={3}>
                    {/* Explicit `ids.input` (not just `id`): both fields sit inside the one shared
                        Field.Root above, so ark-ui's useNumberInput otherwise derives BOTH inputs'
                        DOM id from that single Field context's `ids.control` -- an id collision
                        between the two sibling inputs that `id` alone (a lower-priority fallback)
                        does not override. See task-11f-report.md. */}
                    <NumberInput.Root ids={{ input: 'primary-propensity-lo-input' }} size="sm" min={0} max={100} formatOptions={{ maximumFractionDigits: 0 }} value={String(sf.propensityLo)}
                      onValueChange={(details: NumberInputValueChangeDetails) => setSmartFilter({ propensityLo: Number.isNaN(details.valueAsNumber) ? 0 : details.valueAsNumber })}>
                      <NumberInput.Input aria-label="Primary turnout propensity lower bound" />
                    </NumberInput.Root>
                    <NumberInput.Root ids={{ input: 'primary-propensity-hi-input' }} size="sm" min={0} max={100} formatOptions={{ maximumFractionDigits: 0 }} value={String(sf.propensityHi)}
                      onValueChange={(details: NumberInputValueChangeDetails) => setSmartFilter({ propensityHi: Number.isNaN(details.valueAsNumber) ? 0 : details.valueAsNumber })}>
                      <NumberInput.Input aria-label="Primary turnout propensity upper bound" />
                    </NumberInput.Root>
                  </SimpleGrid>
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm" mb={1}>
                    <LeverLabel text="Your candidate's lane" tip={explainCopy.primaryLane(sf.laneDirection ?? 'progressive', sf.leanThreshold)} />
                  </Field.Label>
                  <RadioGroup.Root
                    size="sm"
                    value={sf.laneDirection ?? 'progressive'}
                    onValueChange={(details: RadioGroupValueChangeDetails) => setSmartFilter({ laneDirection: details.value as 'progressive' | 'moderate' })}>
                    <Stack direction="row" gap={4}>
                      <RadioGroup.Item value="progressive"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Progressive</RadioGroup.ItemText></RadioGroup.Item>
                      <RadioGroup.Item value="moderate"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Moderate</RadioGroup.ItemText></RadioGroup.Item>
                    </Stack>
                  </RadioGroup.Root>
                  <NumberInput.Root mt={2} size="sm" min={0} max={100} formatOptions={{ maximumFractionDigits: 0 }} value={String(sf.leanThreshold)}
                    onValueChange={(details: NumberInputValueChangeDetails) => setSmartFilter({ leanThreshold: Number.isNaN(details.valueAsNumber) ? 0 : details.valueAsNumber })}>
                    <NumberInput.Input aria-label="Lane ideology threshold" />
                  </NumberInput.Root>
                  <Field.HelperText fontSize="xs" color="gray.500" mt={1}>
                    ideology {sf.laneDirection === 'moderate' ? '≤' : '≥'} this (0=cons · 100=prog)
                  </Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm" mb={2}>Issue alignment (optional)</Field.Label>
                  <CheckboxGroup
                    value={sf.issues ?? []}
                    onValueChange={(vals: string[]) => setSmartFilter({ issues: vals as string[] })}
                  >
                    <SimpleGrid columns={{ base: 1, sm: 2 }} gap={2}>
                      {PRIMARY_ISSUES.map((iss) => (
                        <Checkbox.Root key={iss.key} value={iss.key} size="sm"><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <LeverLabel text={iss.label} tip={explainCopy.primaryIssue(iss.label.toLowerCase())} />
                        </Checkbox.Label></Checkbox.Root>
                      ))}
                    </SimpleGrid>
                  </CheckboxGroup>
                </Field.Root>

                <Checkbox.Root
                  size="sm"
                  onCheckedChange={(details) => setSmartFilter({ activismFloor: details.checked === true ? 50 : null })}
                  checked={sf.activismFloor != null}
                ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                  <LeverLabel text="Require high general activism" tip={explainCopy.primaryActivismFloor(sf.activismFloor ?? 50)} />
                </Checkbox.Label></Checkbox.Root>
                {sf.activismFloor != null && (
                  <NumberInput.Root size="sm" min={0} max={100} formatOptions={{ maximumFractionDigits: 0 }} value={String(sf.activismFloor)}
                    onValueChange={(details: NumberInputValueChangeDetails) => setSmartFilter({ activismFloor: Number.isNaN(details.valueAsNumber) ? 0 : details.valueAsNumber })}>
                    <NumberInput.Input aria-label="Activism floor" />
                  </NumberInput.Root>
                )}
              </>
            ) : (
              <>
                <Field.Root>
                  <Field.Label fontSize="sm" mb={1}>
                    <LeverLabel
                      text="Turnout propensity"
                      tip={explainCopy.turnoutPropensityBand(sf.propensityLo, sf.propensityHi)}
                    />
                  </Field.Label>
                  <SimpleGrid columns={2} gap={3}>
                    {/* Explicit `ids.input` (not just `id`): both fields sit inside the one shared
                        Field.Root above, so ark-ui's useNumberInput otherwise derives BOTH inputs'
                        DOM id from that single Field context's `ids.control` -- an id collision
                        between the two sibling inputs that `id` alone (a lower-priority fallback)
                        does not override. See task-11f-report.md. */}
                    <NumberInput.Root
                      ids={{ input: 'general-propensity-lo-input' }}
                      size="sm"
                      min={0}
                      max={100}
                      formatOptions={{ maximumFractionDigits: 0 }}
                      value={String(sf.propensityLo)}
                      onValueChange={(details: NumberInputValueChangeDetails) => setSmartFilter({ propensityLo: Number.isNaN(details.valueAsNumber) ? 0 : details.valueAsNumber })}
                    >
                      <NumberInput.Input aria-label="Turnout propensity lower bound" />
                    </NumberInput.Root>
                    <NumberInput.Root
                      ids={{ input: 'general-propensity-hi-input' }}
                      size="sm"
                      min={0}
                      max={100}
                      formatOptions={{ maximumFractionDigits: 0 }}
                      value={String(sf.propensityHi)}
                      onValueChange={(details: NumberInputValueChangeDetails) => setSmartFilter({ propensityHi: Number.isNaN(details.valueAsNumber) ? 0 : details.valueAsNumber })}
                    >
                      <NumberInput.Input aria-label="Turnout propensity upper bound" />
                    </NumberInput.Root>
                  </SimpleGrid>
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm" mb={1}>
                    <LeverLabel text="Your-side lean" tip={explainCopy.yourSideLean(sf.leanThreshold)} />
                  </Field.Label>
                  <NumberInput.Root
                    size="sm"
                    min={0}
                    max={100}
                    formatOptions={{ maximumFractionDigits: 0 }}
                    value={String(sf.leanThreshold)}
                    onValueChange={(details: NumberInputValueChangeDetails) => setSmartFilter({ leanThreshold: Number.isNaN(details.valueAsNumber) ? 0 : details.valueAsNumber })}
                  >
                    <NumberInput.Input aria-label="Your-side lean threshold" />
                  </NumberInput.Root>
                  <Field.HelperText fontSize="xs" color="gray.500" mt={1}>
                    ideology ≥ this (0=cons · 100=prog)
                  </Field.HelperText>
                </Field.Root>

                {levers.assumptions.persuasionPp > 0 && (
                  <Checkbox.Root
                    size="sm"
                    onCheckedChange={(details) => setSmartFilter({ persuasionMidband: details.checked === true })}
                    checked={sf.persuasionMidband}
                  ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                    <LeverLabel
                      text="Also limit to persuasion mid-band (35–65)"
                      tip={explainCopy.persuasionMidBand()}
                    />
                  </Checkbox.Label></Checkbox.Root>
                )}
              </>
            )}

            <Tooltip content={explainCopy.smartReachableCount()} openDelay={0} showArrow contentProps={{ maxW: "320px" }} positioning={{
              placement: "top"
            }}>
              <Box
                borderLeftWidth="3px"
                borderLeftColor="info.fg"
                bg="gray.50"
                borderRadius="0 8px 8px 0"
                px={4}
                py={2}
                cursor="help"
                tabIndex={0}
              >
                <Text fontSize="sm" color="gray.700">
                  {countReq == null ? (
                    'Choose a district to see how many voters match.'
                  ) : countQuery.error ? (
                    <Text as="span" color="danger.fg">
                      {extractApiMessage(countQuery.error, 'Could not count matching voters.')}
                    </Text>
                  ) : countQuery.data == null ? (
                    'Counting…'
                  ) : countQuery.data < 10 ? (
                    'Fewer than 10 voters match — too few to target.'
                  ) : (
                    <>
                      <b>{fmt(countQuery.data)}</b> reachable voters match this filter.
                    </>
                  )}
                </Text>
              </Box>
            </Tooltip>
          </VStack>
        )}

        {reachable !== null && (
          <Box
            mt={4}
            borderLeftWidth="3px"
            borderLeftColor="info.fg"
            bg="gray.50"
            borderRadius="0 8px 8px 0"
            px={4}
            py={2}
          >
            <Text fontSize="sm" color="gray.700">
              <b>{fmt(reachable)} reachable voters</b> in the selected cohort.
            </Text>
          </Box>
        )}
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Effect assumptions */}
      {/* ---------------------------------------------------------------- */}
      <Card eyebrow="Effect assumptions">
        <NumberField
          label="Contact → turnout lift"
          tip={explainCopy.turnoutLift(
            levers.assumptions.turnoutLiftPp,
            BAND_LOWER,
            BAND_UPPER,
            null,
            contacts
          )}
          value={levers.assumptions.turnoutLiftPp}
          onChange={(v) => setAssumptions({ turnoutLiftPp: v })}
          helper="assumption, not a measurement — sourced default"
        />

        <Accordion.Root collapsible mt={2}>
          <Accordion.Item border="none" value='item-0'>
            <Accordion.ItemTrigger px={0}>
              <Text fontSize="sm" fontWeight="semibold" flex="1" textAlign="left">
                Advanced
              </Text>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent px={0} pb={2}><Accordion.ItemBody>
                <VStack align="stretch" gap={4}>
                  <NumberField
                    label="Advanced: persuasion shift"
                    tip={explainCopy.persuasion(levers.assumptions.persuasionPp)}
                    value={levers.assumptions.persuasionPp}
                    onChange={(v) => setAssumptions({ persuasionPp: v })}
                    helper="off by default — the program's use case is blue-side turnout"
                    muted
                  />
                  <NumberField
                    label="Advanced: raw turnout delta"
                    tip={explainCopy.advancedTurnoutDelta()}
                    value={levers.advanced?.turnoutDeltaPp ?? 0}
                    onChange={(v) => setAdvanced({ turnoutDeltaPp: v })}
                    muted
                  />
                  <NumberField
                    label="Advanced: raw persuasion delta"
                    tip={explainCopy.advancedTurnoutDelta()}
                    value={levers.advanced?.persuasionDeltaPp ?? 0}
                    onChange={(v) => setAdvanced({ persuasionDeltaPp: v })}
                    muted
                  />
                </VStack>
              </Accordion.ItemBody></Accordion.ItemContent>
          </Accordion.Item>
        </Accordion.Root>
      </Card>

      <Button colorPalette="brand" loading={isRunning} onClick={onRun} w="full">
        Run simulation
      </Button>
    </VStack>
  );
}
